import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, Users, Building2, Briefcase, Mail, Phone, Tag, 
  Calendar, DollarSign, Clock, Sparkles, Plus, Search, ChevronRight, 
  CheckCircle2, AlertTriangle, Check, ArrowUpRight, Activity, Ban, 
  HelpCircle, ShieldAlert, HeartPulse, Milestone, MessageSquare, 
  ListChecks, History, PlayCircle, Info, X, Trash2, Shield, Eye, 
  Edit3, Award, ExternalLink, ThumbsUp, Send, UserCheck, FileText,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigation } from '../context/NavigationContext';
import { useCustomerDashboard, useUpdateCustomer } from '../hooks/customers/useCustomers';
import { mapCustomerDashboard } from '../utils/dashboardMappers';
import { PageLoading } from './common/PageLoading';
import { PageError } from './common/PageError';
import { mapStatusToLabel } from '../utils/statusLabels';

interface ClienteWorkspaceViewProps {
  setCurrentView: (view: any) => void;
}

export default function ClienteWorkspaceView({ setCurrentView }: ClienteWorkspaceViewProps) {
  const { globalFilters, setGlobalFilters } = useNavigation();
  
  const clientId = String(globalFilters.clientId || '');

  // ───── Dashboard API query ─────
  const { data: rawDashboard, isLoading, isError, error } = useCustomerDashboard(clientId);
  const updateCustomerMutation = useUpdateCustomer();

  // Mapped dashboard state
  const dashboard = useMemo(() => mapCustomerDashboard(rawDashboard), [rawDashboard]);

  // Health Score state (local interactive)
  const [healthScore, setHealthScore] = useState({
    value: 94,
    status: 'Excelente',
    trend: 'Estável (+2% este mês)',
    lastUpdated: 'Atualizado há 1 hora'
  });

  // State of all interactive items
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [isNewDocModalOpen, setIsNewDocModalOpen] = useState(false);
  
  // Tasks state (local interactive - kept as mock)
  const [tasks, setTasks] = useState<any[]>([
    { id: 101, title: 'Revisar escopo técnico do aditivo Q4', priority: 'Alta', status: 'TODO', dueDate: 'Amanhã' },
    { id: 102, title: 'Agendar call de alinhamento com engenharia', priority: 'Média', status: 'TODO', dueDate: 'Em 3 dias' },
    { id: 103, title: 'Enviar faturamento pro-forma para o financeiro', priority: 'Baixa', status: 'DONE', dueDate: 'Ontem' },
    { id: 104, title: 'Validar protótipo de integração com a API Cyzor', priority: 'Alta', status: 'TODO', dueDate: 'Em 5 dias' }
  ]);

  // ───── Derived data from dashboard ─────
  const client = useMemo(() => {
    if (!dashboard) return null;
    return {
      id: dashboard.id,
      name: dashboard.name,
      email: dashboard.contacts?.[0]?.email || dashboard.name + '@empresa.com',
      phone: dashboard.contacts?.[0]?.phone || '(11) 99999-0000',
      role: dashboard.contacts?.[0]?.role || 'Contato Principal',
      status: dashboard.statusLabel || 'Ativo',
      industry: dashboard.industry || '',
      companyId: dashboard.id,
      clientSince: dashboard.createdAt ? new Date(dashboard.createdAt).toLocaleDateString('pt-BR') : 'N/A',
      responsibleName: 'João Silva',
      responsibleRole: 'Account Manager Senior',
      lastContactDate: 'Hoje às 10:30h (Reunião Técnica)',
      notes: '',
      tags: dashboard.tags || ['VIP'],
    };
  }, [dashboard]);

  const _statusLabel = mapStatusToLabel(client?.status);

  const company = useMemo(() => ({
    id: dashboard?.id || 1,
    name: dashboard?.name || 'Empresa',
    industry: dashboard?.industry || 'Tecnologia & Inovação'
  }), [dashboard]);

  // Projects from dashboard
  const projects = useMemo(() => {
    if (!dashboard?.projects) return [];
    const statusMap: Record<string, string> = { ACTIVE: 'Em Andamento', PLANNED: 'Planejamento', COMPLETED: 'Concluído', CANCELLED: 'Cancelado', ON_HOLD: 'Pausado' };
    return dashboard.projects.map((p: any) => ({
      id: p.id,
      name: p.name,
      status: statusMap[p.status] || p.status || 'Planejamento',
      progress: p.progress || 0,
      dueDate: p.endDate || 'N/A',
      owner: p.owner || 'Não definido',
      role: p.role || '',
    }));
  }, [dashboard]);

  // Documents from dashboard
  const documents = useMemo(() => {
    if (!dashboard?.documents) return [];
    return dashboard.documents.map((d: any) => ({
      id: d.id,
      title: d.title || d.name || 'Documento',
      type: d.type || 'PDF',
      size: d.size || '0 KB',
      date: d.date || '',
      category: d.category || 'Documento',
    }));
  }, [dashboard]);

  // Team members from contacts
  const teamMembers = useMemo(() => {
    if (!dashboard?.contacts || dashboard.contacts.length === 0) {
      return [{ name: 'João Silva', role: 'Account Manager', responsibility: 'Líder do Relacionamento', initials: 'JS', bg: 'bg-[#0F172A] text-white' }];
    }
    const bgColors = ['bg-[#0F172A] text-white', 'bg-indigo-500 text-white', 'bg-emerald-500 text-white', 'bg-amber-500 text-white'];
    return dashboard.contacts.map((c: any, idx: number) => ({
      name: c.name || `${c.firstName} ${c.lastName || ''}`.trim() || 'Contato',
      role: c.role || 'Contato',
      responsibility: 'Membro da Equipe',
      initials: (c.firstName || c.name || 'C').charAt(0).toUpperCase(),
      bg: bgColors[idx % bgColors.length],
    }));
  }, [dashboard]);

  // Timeline from history
  const [timeline, setTimeline] = useState<any[]>([]);
  
  useEffect(() => {
    if (dashboard?.history && dashboard.history.length > 0) {
      const mapped = dashboard.history.map((h: any, idx: number) => ({
        id: h.id || Date.now() + idx,
        date: h.time ? new Date(h.time).toLocaleString('pt-BR') : 'N/A',
        title: h.action,
        desc: h.action,
        type: 'atividade',
        icon: Activity,
        iconColor: 'text-slate-600 bg-slate-50',
      }));
      setTimeline(mapped);
    }
  }, [dashboard]);

  // Toast notifications
  const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const triggerToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // AI Chat states
  const [aiChatText, setAiChatText] = useState('');
  const [aiChatMessages, setAiChatMessages] = useState<any[]>([
    { sender: 'assistant', text: 'Olá! Sou seu Consultor Cyzor IA. Analisei os dados de relacionamento com este cliente. Posso sugerir ações para expandir esta conta ou mitigar riscos. Em que posso te apoiar hoje?' }
  ]);
  const [isAiTyping, setIsAiTyping] = useState(false);

  // New Timeline Comment state
  const [newComment, setNewComment] = useState('');

  // Handle addition of comment
  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const newLog = {
      id: Date.now(),
      date: 'Agora mesmo',
      title: 'Nota interna adicionada',
      desc: newComment,
      type: 'comentario',
      icon: MessageSquare,
      iconColor: 'text-indigo-600 bg-indigo-50'
    };

    setTimeline([newLog, ...timeline]);
    setNewComment('');
    triggerToast('success', 'Nota registrada com sucesso na Timeline do cliente!');
  };

  // Handle tasks toggle
  const toggleTaskStatus = (id: number) => {
    setTasks(prev => prev.map(t => {
      if (t.id === id) {
        const nextStatus = t.status === 'DONE' ? 'TODO' : 'DONE';
        if (nextStatus === 'DONE') {
          const taskLog = {
            id: Date.now(),
            date: 'Agora mesmo',
            title: `Tarefa concluída por você`,
            desc: `"${t.title}" foi marcada como finalizada no painel de atividades.`,
            type: 'atividade',
            icon: CheckCircle2,
            iconColor: 'text-emerald-500 bg-emerald-50'
          };
          setTimeline(prevTimeline => [taskLog, ...prevTimeline]);
          triggerToast('success', 'Tarefa marcada como concluída!');
        }
        return { ...t, status: nextStatus };
      }
      return t;
    }));
  };

  // Form states for adding items
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('Média');

  const handleCreateTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;

    const newTaskObj = {
      id: Date.now(),
      title: newTaskTitle,
      priority: newTaskPriority,
      status: 'TODO',
      dueDate: 'Em breve'
    };

    setTasks([newTaskObj, ...tasks]);
    setNewTaskTitle('');
    setIsNewTaskModalOpen(false);
    triggerToast('success', 'Nova atividade agendada!');

    const timelineLog = {
      id: Date.now(),
      date: 'Agora mesmo',
      title: 'Nova atividade de follow-up criada',
      desc: `A tarefa "${newTaskTitle}" com prioridade ${newTaskPriority} foi criada por você.`,
      type: 'atividade',
      icon: ListChecks,
      iconColor: 'text-slate-600 bg-slate-50'
    };
    setTimeline(prev => [timelineLog, ...prev]);
  };

  // Form states for creating project
  const [newProjName, setNewProjName] = useState('');
  const [newProjDueDate, setNewProjDueDate] = useState('');

  const handleCreateProject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjName.trim()) return;

    const newProjObj = {
      id: Date.now(),
      name: newProjName,
      status: 'Planejamento',
      progress: 0,
      dueDate: newProjDueDate || '31 Dez 2026',
      owner: 'João Silva',
      role: 'Account Manager'
    };

    // Optimistic update: add to local array
    const prevProjects = [...projects];
    // We can't directly set projects since it's derived via useMemo, 
    // but we track new ones via a separate state
    setLocalProjects(prev => [newProjObj, ...prev]);
    setNewProjName('');
    setNewProjDueDate('');
    setIsNewProjectModalOpen(false);
    triggerToast('success', 'Novo projeto criado para o cliente!');

    const timelineLog = {
      id: Date.now(),
      date: 'Agora mesmo',
      title: 'Novo projeto iniciado',
      desc: `O projeto "${newProjName}" foi iniciado na trilha corporativa do cliente.`,
      type: 'projeto',
      icon: Briefcase,
      iconColor: 'text-indigo-600 bg-indigo-50'
    };
    setTimeline(prev => [timelineLog, ...prev]);
  };

  // Local projects state (for newly created ones before dashboard refresh)
  const [localProjects, setLocalProjects] = useState<any[]>([]);
  const allProjects = useMemo(() => [...localProjects, ...projects], [localProjects, projects]);

  // Form states for creating document
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocCategory, setNewDocCategory] = useState('Contrato');

  const handleCreateDoc = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocTitle.trim()) return;

    const newDocObj = {
      id: Date.now(),
      title: newDocTitle,
      type: 'PDF',
      size: '1.2 MB',
      date: 'Hoje',
      category: newDocCategory
    };

    setLocalDocuments(prev => [newDocObj, ...prev]);
    setNewDocTitle('');
    setIsNewDocModalOpen(false);
    triggerToast('success', 'Documento anexado à conta!');

    const timelineLog = {
      id: Date.now(),
      date: 'Agora mesmo',
      title: 'Documento vinculado à conta',
      desc: `O arquivo "${newDocTitle}" (${newDocCategory}) foi carregado e está disponível no Workspace.`,
      type: 'documento',
      icon: FileText,
      iconColor: 'text-indigo-500 bg-indigo-50'
    };
    setTimeline(prev => [timelineLog, ...prev]);
  };

  const [localDocuments, setLocalDocuments] = useState<any[]>([]);
  const allDocuments = useMemo(() => [...localDocuments, ...documents], [localDocuments, documents]);

  // Edit Client Information handler
  const [editFormData, setEditFormData] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    status: '',
    notes: ''
  });

  useEffect(() => {
    if (client) {
      setEditFormData({
        name: client.name || '',
        role: client.role || '',
        email: client.email || '',
        phone: client.phone || '',
        status: client.status || 'Ativo',
        notes: client.notes || ''
      });
    }
  }, [client]);

  const handleSaveClientInfo = (e: React.FormEvent) => {
    e.preventDefault();
    // Call the update mutation
    if (clientId) {
      updateCustomerMutation.mutate({
        id: clientId,
        data: {
          name: editFormData.name,
          contactEmail: editFormData.email,
          contactPhone: editFormData.phone,
          status: editFormData.status,
        }
      });
    }
    setIsEditModalOpen(false);
    triggerToast('success', 'Cadastro do cliente atualizado no Workspace!');

    const updateLog = {
      id: Date.now(),
      date: 'Agora mesmo',
      title: 'Cadastro do cliente atualizado',
      desc: 'Alterações efetuadas na ficha cadastral e de relacionamento corporativo.',
      type: 'status',
      icon: Edit3,
      iconColor: 'text-slate-600 bg-slate-50'
    };
    setTimeline(prev => [updateLog, ...prev]);
  };

  // Handle AI Chat submissions
  const handleSendAiMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiChatText.trim()) return;

    const userMsg = { sender: 'user', text: aiChatText };
    setAiChatMessages(prev => [...prev, userMsg]);
    setAiChatText('');
    setIsAiTyping(true);

    setTimeout(() => {
      let aiText = `Com base nas interações com a **${company.name}**, notei um sentimento altamente positivo nas últimas reuniões lideradas por ${client?.name}. 

**Recomendações estratégicas:**
1. **Risco de Churn**: Menor que 5%. Os pagamentos estão totalmente em dia e o Health Score de **${healthScore.value}%** é excelente.
2. **Upsell**: O cliente já sinalizou que precisa expandir o uso da API no Q4. Sugiro apresentar o plano Enterprise Pro na próxima reunião comercial.
3. **Próximo Passo**: Concluir o aditivo técnico de escopo que está listado como pendência na lista de atividades.

Deseja que eu elabore um rascunho de e-mail de proposta ou agende uma reunião automática?`;
      
      const query = userMsg.text.toLowerCase();
      if (query.includes('risco') || query.includes('churn')) {
        aiText = `**Análise de Riscos para ${company.name}:**
- O principal risco identificado anteriormente era a latência das APIs de integração. Com o kick-off do projeto de migração de servidores legados (concluído recentemente com sucesso), a satisfação técnica subiu significativamente.
- **Probabilidade de Churn**: Praticamente nula (menor que 3%).
- **Alerta de Atenção**: Nenhuma fatura atrasada ou pendência financeira crítica ativa.`;
      } else if (query.includes('upsell') || query.includes('venda') || query.includes('proposta')) {
        aiText = `**Oportunidade Comercial de Upsell:**
- **Ponto de Entrada**: ${client?.name} demonstrou alto interesse no módulo automatizado de conciliação financeira do CYZOR no call técnico de hoje.
- **Ticket Adicional Estimado**: + R$ 4.500,00 / mês.
- **Plano de Ação**: Enviar a proposta comercial estendida anexando o escopo de integração v2 que já consta na aba de documentos deste Workspace.`;
      }

      setAiChatMessages(prev => [...prev, { sender: 'assistant', text: aiText }]);
      setIsAiTyping(false);
    }, 1500);
  };

  // ───── Loading / Error states ─────
  if (isLoading) return <PageLoading message="Carregando dados do cliente..." />;
  if (isError) return <PageError message={(error as any)?.message || 'Erro ao carregar dados do cliente'} onRetry={() => window.location.reload()} />;
  if (!client) return <PageLoading message="Preparando workspace..." />;

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-8 animate-in fade-in duration-500 relative px-2 sm:px-4 md:px-8">
      
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          BREADCRUMBS & TOP BAR
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex items-center gap-3 text-xs font-bold text-neutral-400 text-left">
        <button 
          onClick={() => {
            setGlobalFilters(prev => ({ ...prev, clientId: undefined }));
            setCurrentView('clientes');
          }}
          className="hover:text-neutral-900 flex items-center gap-1 transition-colors"
        >
          Clientes
        </button>
        <ChevronRight size={10} />
        <span className="text-neutral-900 font-black">{client.name}</span>
      </div>

      {/* Toast Alert */}
      <AnimatePresence>
        {toast && (
          <motion.div 
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl border text-xs font-bold ${
              toast.type === 'success' 
                ? 'bg-emerald-50 border-emerald-100 text-emerald-800' 
                : 'bg-rose-50 border-rose-100 text-rose-800'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle2 className="text-emerald-500" size={16} /> : <AlertCircle className="text-rose-500" size={16} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- HERO AREA --- */}
      <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.02)] flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          
          {/* Left Side: Avatar, Name and Meta info */}
          <div className="flex items-start gap-4 sm:gap-5">
            <button 
              onClick={() => {
                setGlobalFilters(prev => ({ ...prev, clientId: undefined }));
                setCurrentView('clientes');
              }}
              className="p-2.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-all border border-slate-100 mr-1 mt-1 cursor-pointer"
              title="Voltar para a Lista de Clientes"
            >
              <ArrowLeft size={16} strokeWidth={2.5} />
            </button>
            
            <div className="w-16 h-16 rounded-2xl bg-indigo-50/80 text-indigo-700 border border-indigo-100/50 flex items-center justify-center font-black text-xl shadow-inner uppercase">
              {client.name ? client.name.charAt(0) : 'C'}
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl sm:text-2xl font-black text-[#0F172A] tracking-tight">{client.name}</h1>
                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${
                  _statusLabel === 'Ativo' 
                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-100/80' 
                    : _statusLabel === 'Lead'
                    ? 'bg-blue-50 text-blue-600 border border-blue-100/80'
                    : 'bg-slate-100 text-slate-500 border border-slate-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    _statusLabel === 'Ativo' ? 'bg-emerald-500' : _statusLabel === 'Lead' ? 'bg-blue-500' : 'bg-slate-400'
                  }`} />
                  {_statusLabel}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-y-1.5 gap-x-4 text-xs font-semibold text-[#64748B]">
                <span className="flex items-center gap-1.5">
                  <Building2 size={13} className="text-slate-400" />
                  <span className="text-[#0F172A] font-bold">{company.name}</span>
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1.5">
                  <Briefcase size={13} className="text-slate-400" />
                  <span>{client.role}</span>
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-slate-400" />
                  <span>Cliente desde {client.clientSince}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right Side: Quick Action buttons */}
          <div className="flex flex-wrap items-center gap-2.5 sm:self-end lg:self-center">
            <button 
              onClick={() => setIsEditModalOpen(true)}
              className="px-4 py-2 text-xs font-bold bg-white text-[#0F172A] hover:bg-slate-50 rounded-xl transition-all border border-slate-200/80 shadow-xs cursor-pointer flex items-center gap-1.5"
            >
              <Edit3 size={13} />
              <span>Editar Cadastro</span>
            </button>
            <button 
              onClick={() => setIsNewProjectModalOpen(true)}
              className="px-4 py-2 text-xs font-bold bg-white text-[#0F172A] hover:bg-slate-50 rounded-xl transition-all border border-slate-200/80 shadow-xs cursor-pointer flex items-center gap-1.5"
            >
              <Plus size={13} />
              <span>Novo Projeto</span>
            </button>
            <button 
              onClick={() => setIsNewDocModalOpen(true)}
              className="px-4 py-2 text-xs font-bold bg-white text-[#0F172A] hover:bg-slate-50 rounded-xl transition-all border border-slate-200/80 shadow-xs cursor-pointer flex items-center gap-1.5"
            >
              <FileText size={13} />
              <span>Anexar Proposta</span>
            </button>
            <button 
              onClick={() => setIsNewTaskModalOpen(true)}
              className="px-4 py-2 text-xs font-bold bg-[#0F172A] text-white hover:bg-[#1E293B] rounded-xl transition-all shadow-xs cursor-pointer flex items-center gap-1.5"
            >
              <CheckCircle2 size={13} />
              <span>Nova Tarefa</span>
            </button>
          </div>
        </div>

        {/* Divider & Metadata tags */}
        <div className="h-px bg-[#0F172A05]" />

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Mail size={14} />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">E-mail</span>
              <span className="font-bold text-[#0F172A] truncate">{client.email}</span>
            </div>
          </div>
          <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Phone size={14} />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Telefone</span>
              <span className="font-bold text-[#0F172A]">{client.phone}</span>
            </div>
          </div>
          <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
              <UserCheck size={14} />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Responsável Comercial</span>
              <span className="font-bold text-[#0F172A]">{client.responsibleName}</span>
            </div>
          </div>
          <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Activity size={14} />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Último Contato Comercial</span>
              <span className="font-bold text-[#0F172A] truncate">{client.lastContactDate}</span>
            </div>
          </div>
        </div>
      </div>

      {/* --- FIRST SECTION: RESUMO EXECUTIVO --- */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white border border-[#0F172A0A] p-4.5 rounded-[24px] shadow-[0_2px_12px_rgba(15,23,42,0.01)] flex flex-col gap-2 relative overflow-hidden group">
          <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
            <Briefcase size={14} />
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Projetos Ativos</span>
            <span className="text-xl font-black text-[#0F172A] mt-0.5">{allProjects.filter(p => p.status !== 'Concluído').length}</span>
            <span className="text-[10px] text-slate-500 font-medium mt-1">{allProjects.filter(p => p.status === 'Concluído').length} Concluído</span>
          </div>
        </div>

        <div className="bg-white border border-[#0F172A0A] p-4.5 rounded-[24px] shadow-[0_2px_12px_rgba(15,23,42,0.01)] flex flex-col gap-2 relative overflow-hidden group">
          <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
            <AlertTriangle size={14} />
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Financeiro em Aberto</span>
            <span className="text-xl font-black text-rose-600 mt-0.5">R$ 1.250</span>
            <span className="text-[10px] text-slate-500 font-medium mt-1">Vence dia 10</span>
          </div>
        </div>

        <div className="bg-white border border-[#0F172A0A] p-4.5 rounded-[24px] shadow-[0_2px_12px_rgba(15,23,42,0.01)] flex flex-col gap-2 relative overflow-hidden group">
          <div className="w-7 h-7 rounded-lg bg-[#E2F0D9] text-[#385723] flex items-center justify-center">
            <DollarSign size={14} />
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Último Faturamento</span>
            <span className="text-xl font-black text-[#0F172A] mt-0.5">R$ 15.000</span>
            <span className="text-[10px] text-slate-500 font-medium mt-1">Recebido há 15 dias</span>
          </div>
        </div>

        <div className="bg-white border border-[#0F172A0A] p-4.5 rounded-[24px] shadow-[0_2px_12px_rgba(15,23,42,0.01)] flex flex-col gap-2 relative overflow-hidden group">
          <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
            <Calendar size={14} />
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Próxima Reunião</span>
            <span className="text-xl font-black text-blue-600 mt-0.5">Amanhã</span>
            <span className="text-[10px] text-slate-500 font-medium mt-1">14:00h - Escopo</span>
          </div>
        </div>

        <div className="bg-white border border-[#0F172A0A] p-4.5 rounded-[24px] shadow-[0_2px_12px_rgba(15,23,42,0.01)] flex flex-col gap-2 relative overflow-hidden group col-span-1">
          <div className="flex items-center justify-between">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <HeartPulse size={14} />
            </div>
            <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">{healthScore.status}</span>
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Relationship Health</span>
            <span className="text-xl font-black text-[#0F172A] mt-0.5">{healthScore.value}%</span>
            <span className="text-[10px] text-slate-400 font-medium truncate mt-1">{healthScore.trend}</span>
          </div>
        </div>

        <div className="bg-white border border-[#0F172A0A] p-4.5 rounded-[24px] shadow-[0_2px_12px_rgba(15,23,42,0.01)] flex flex-col gap-2 relative overflow-hidden group col-span-1">
          <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <Milestone size={14} />
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Próxima Ação</span>
            <span className="text-xs font-black text-[#0F172A] mt-1.5 line-clamp-2 leading-tight">Enviar aditivo de escopo técnico revisado</span>
          </div>
        </div>
      </div>

      {/* --- SECOND SECTION: TIMELINE & CHAT ENGINE GRID --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Timeline (Width: 2/3) */}
        <div className="lg:col-span-2 bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.01)] flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <History size={15} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#0F172A]">Timeline de Relacionamento</h2>
                <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Histórico centralizado de atividades e notas internas da equipe</p>
              </div>
            </div>
            <span className="text-[10px] font-bold text-[#64748B] bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">Atualizado agora</span>
          </div>

          <form onSubmit={handleAddComment} className="bg-slate-50/50 border border-slate-100 p-4 rounded-2xl flex flex-col gap-3">
            <textarea
              rows={2}
              placeholder="Digite um comentário ou nota rápida sobre o último contato..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="w-full text-xs bg-transparent focus:outline-none resize-none text-slate-700 leading-relaxed placeholder-slate-400"
            />
            <div className="flex items-center justify-between border-t border-slate-100 pt-2.5">
              <span className="text-[9px] text-[#64748B] font-semibold">Seu comentário ficará registrado publicamente nesta timeline</span>
              <button 
                type="submit"
                className="bg-black hover:bg-[#1E293B] text-white px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
              >
                <Send size={10} />
                <span>Registrar Nota</span>
              </button>
            </div>
          </form>

          <div className="relative pl-6 border-l border-slate-100 flex flex-col gap-6 ml-3 mt-2">
            {timeline.length === 0 && (
              <div className="text-xs text-slate-400 font-semibold text-center py-4">
                Nenhum histórico de atividade registrado ainda.
              </div>
            )}
            {timeline.map((item, idx) => {
              const IconComp = item.icon || Activity;
              return (
                <div key={item.id} className="relative group">
                  <div className={`absolute -left-[37px] top-0.5 w-6 h-6 rounded-full border border-white shadow-xs flex items-center justify-center ${item.iconColor || 'bg-slate-100 text-slate-600'}`}>
                    <IconComp size={10} />
                  </div>
                  <div className="flex flex-col gap-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400">{item.date}</span>
                      <span className="text-[10px] text-slate-300">•</span>
                      <span className="text-xs font-black text-[#0F172A]">{item.title}</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed font-semibold">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CYZOR IA & Health Score Panel */}
        <div className="flex flex-col gap-8">
          
          <div className="bg-slate-900 text-white rounded-[32px] p-6 sm:p-8 shadow-xl flex flex-col gap-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/15 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex items-center gap-2.5 relative z-10">
              <div className="w-8 h-8 rounded-xl bg-white/10 text-white flex items-center justify-center shadow-inner">
                <Sparkles size={15} className="animate-pulse text-indigo-300" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white tracking-tight">Cyzor Consultor IA</h2>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Visão Executiva & Sugestões</p>
              </div>
            </div>

            <div className="flex flex-col gap-4 text-xs font-medium relative z-10">
              <div className="bg-white/5 border border-white/10 p-3.5 rounded-2xl flex flex-col gap-2.5">
                <div className="flex items-center gap-2 text-indigo-300 font-bold">
                  <Award size={13} />
                  <span>Análise de Relacionamento</span>
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed font-semibold">
                  A conta está em patamar de **alto engajamento**. O sentiment score das reuniões subiu após a resolução ágil de infraestrutura anterior.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div className="bg-white/5 border border-white/5 p-2 rounded-xl flex flex-col gap-0.5">
                  <span className="text-slate-400 font-semibold">Oportunidade</span>
                  <span className="text-emerald-400 font-bold flex items-center gap-0.5">Upsell Plano Pro</span>
                </div>
                <div className="bg-white/5 border border-white/5 p-2 rounded-xl flex flex-col gap-0.5">
                  <span className="text-slate-400 font-semibold">Risco de Churn</span>
<span className="text-slate-300 font-bold">{'Inexistente (<3%)'}</span>
                </div>
                <div className="bg-white/5 border border-white/5 p-2 rounded-xl flex flex-col gap-0.5">
                  <span className="text-slate-400 font-semibold">Sentimento</span>
                  <span className="text-indigo-400 font-bold">Altamente Positivo</span>
                </div>
                <div className="bg-white/5 border border-white/5 p-2 rounded-xl flex flex-col gap-0.5">
                  <span className="text-slate-400 font-semibold">Renovação Q4</span>
                  <span className="text-indigo-400 font-bold">98% Probabilidade</span>
                </div>
              </div>

              <div className="h-px bg-white/10 my-1" />
              
              <div className="flex flex-col gap-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pergunte à Inteligência Artificial</span>
                <div className="max-h-[140px] overflow-y-auto flex flex-col gap-2.5 pr-1 bg-white/5 p-3 rounded-xl border border-white/5">
                  {aiChatMessages.map((msg, index) => (
                    <div key={index} className={`flex flex-col gap-1 ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                      <span className="text-[8px] font-bold text-slate-500 uppercase">{msg.sender === 'user' ? 'Você' : 'Cyzor IA'}</span>
                      <div className={`p-2 rounded-xl text-[10px] leading-relaxed max-w-[90%] font-semibold ${msg.sender === 'user' ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white/5 text-slate-200 rounded-tl-none'}`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {isAiTyping && (
                    <div className="text-[10px] text-slate-400 font-bold italic animate-pulse flex items-center gap-1.5">
                      <Sparkles size={10} className="animate-spin text-indigo-300" />
                      <span>Cyzor IA analisando dados do cliente...</span>
                    </div>
                  )}
                </div>

                <form onSubmit={handleSendAiMessage} className="relative mt-1">
                  <input
                    type="text"
                    placeholder="Ex: Como aumentar o ticket deste cliente?"
                    value={aiChatText}
                    onChange={(e) => setAiChatText(e.target.value)}
                    className="w-full bg-white/10 border border-white/15 rounded-xl pl-3 pr-10 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 placeholder-slate-500 font-semibold"
                  />
                  <button 
                    type="submit"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-indigo-300 hover:text-white transition-colors cursor-pointer"
                  >
                    <Send size={12} />
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Account Team */}
          <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.01)] flex flex-col gap-5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Users size={15} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#0F172A]">Equipe Responsável</h2>
                <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Colaboradores envolvidos no suporte e operação da conta</p>
              </div>
            </div>

            <div className="flex flex-col gap-3.5">
              {teamMembers.map((member, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded-2xl hover:bg-slate-50/50 border border-transparent hover:border-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl font-black text-xs flex items-center justify-center shadow-xs ${member.bg}`}>
                      {member.initials}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-[#0F172A]">{member.name}</span>
                      <span className="text-[9px] font-semibold text-[#64748B] mt-0.5">{member.role}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100/50">
                      {member.responsibility}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* --- THIRD SECTION: ACTIVE PROJECTS & PENDENCIES --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Active Projects */}
        <div className="lg:col-span-2 bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.01)] flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Briefcase size={15} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#0F172A]">Projetos Vinculados</h2>
                <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Cronograma ativo e status de entrega de projetos contratados</p>
              </div>
            </div>
            
            <button 
              onClick={() => setIsNewProjectModalOpen(true)}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
            >
              <Plus size={12} />
              <span>Novo Projeto</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[500px]">
              <thead>
                <tr className="border-b border-slate-100 text-[9px] font-bold text-slate-400 uppercase tracking-wider pb-2">
                  <th className="pb-3">Projeto</th>
                  <th className="pb-3">Responsável</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Prazo</th>
                  <th className="pb-3">Progresso</th>
                  <th className="pb-3 text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/60">
                {allProjects.map((proj) => (
                  <tr key={proj.id} className="hover:bg-slate-50/40 transition-colors">
                    <td className="py-3 font-bold text-xs text-[#0F172A]">{proj.name}</td>
                    <td className="py-3 text-xs font-semibold text-slate-600">
                      <div className="flex flex-col">
                        <span>{proj.owner}</span>
                        <span className="text-[9px] text-[#64748B] font-medium">{proj.role}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`text-[9px] font-black px-2 py-0.5 rounded-full inline-block ${
                        proj.status === 'Concluído' 
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                          : proj.status === 'Em Andamento'
                          ? 'bg-blue-50 text-blue-600 border border-blue-100'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {proj.status}
                      </span>
                    </td>
                    <td className="py-3 text-xs font-semibold text-slate-500">{proj.dueDate}</td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${proj.status === 'Concluído' ? 'bg-emerald-500' : 'bg-indigo-600'}`}
                            style={{ width: `${proj.progress}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-bold text-slate-700">{proj.progress}%</span>
                      </div>
                    </td>
                    <td className="py-3 text-right">
                      <button 
                        onClick={() => {
                          setGlobalFilters(prev => ({ ...prev, projectId: proj.id }));
                          setCurrentView('projetos');
                        }}
                        className="text-[10px] font-bold text-slate-700 hover:text-black border border-slate-200 hover:border-black rounded-lg px-2.5 py-1.5 transition-colors cursor-pointer inline-flex items-center gap-1 bg-white"
                      >
                        <span>Abrir</span>
                        <ExternalLink size={10} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tasks Checklist */}
        <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.01)] flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <ListChecks size={15} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#0F172A]">Atividades Pendentes</h2>
                <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Tarefas táticas associadas ao cliente</p>
              </div>
            </div>
            
            <button 
              onClick={() => setIsNewTaskModalOpen(true)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 bg-slate-50 border border-slate-100 cursor-pointer"
              title="Nova Atividade"
            >
              <Plus size={14} />
            </button>
          </div>

          <div className="flex flex-col gap-2.5">
            {tasks.map((task) => (
              <div 
                key={task.id} 
                className={`flex items-start gap-3 p-3 rounded-2xl border transition-all ${
                  task.status === 'DONE' 
                    ? 'bg-slate-50/50 border-slate-100/70 text-slate-400' 
                    : 'bg-white border-slate-100 hover:border-slate-200 shadow-2xs'
                }`}
              >
                <button 
                  onClick={() => toggleTaskStatus(task.id)}
                  className={`mt-0.5 w-4 h-4 rounded-md border flex items-center justify-center transition-all cursor-pointer ${
                    task.status === 'DONE' 
                      ? 'bg-emerald-500 border-emerald-500 text-white' 
                      : 'border-slate-300 hover:border-indigo-500 bg-white'
                  }`}
                >
                  {task.status === 'DONE' && <Check size={10} strokeWidth={3} />}
                </button>
                
                <div className="flex flex-col gap-1 flex-1 text-left">
                  <span className={`text-xs font-semibold leading-relaxed ${task.status === 'DONE' ? 'line-through text-slate-400' : 'text-[#0F172A]'}`}>
                    {task.title}
                  </span>
                  
                  <div className="flex items-center justify-between mt-1 text-[9px] font-bold">
                    <span className={`px-1.5 py-0.5 rounded-md ${
                      task.priority === 'Alta' 
                        ? 'bg-rose-50 text-rose-600' 
                        : task.priority === 'Média'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {task.priority}
                    </span>
                    <span className="text-slate-400">Prazo: {task.dueDate}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- FOURTH SECTION: FINANCIAL RELATIONSHIP & DOCUMENTS GRID --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Financial Panel */}
        <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.01)] flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <DollarSign size={15} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#0F172A]">Resumo Financeiro da Conta</h2>
                <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Histórico financeiro e pagamentos recorrentes contratuais</p>
              </div>
            </div>

            <button 
              onClick={() => setCurrentView('financeiro')}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              <span>Abrir Financeiro</span>
              <ArrowUpRight size={12} />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50/60 p-4 rounded-2xl border border-slate-100">
            <div className="flex flex-col">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Receita Total</span>
              <span className="text-sm font-black text-slate-800 mt-1">R$ 180.000</span>
            </div>
            <div className="flex flex-col border-l border-slate-100 pl-3">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Valor em Aberto</span>
              <span className="text-sm font-black text-rose-600 mt-1">R$ 1.250</span>
            </div>
            <div className="flex flex-col border-l border-slate-100 pl-3">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Último Recebimento</span>
              <span className="text-sm font-black text-slate-800 mt-1">R$ 15.000</span>
            </div>
            <div className="flex flex-col border-l border-slate-100 pl-3">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Próxima Fatura</span>
              <span className="text-sm font-black text-indigo-600 mt-1">10/08/2026</span>
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Faturas Recentes</span>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between text-xs p-2 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="font-bold text-slate-700">Mensalidade recorrente Julho</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-500 font-semibold">14/07/2026</span>
                  <span className="font-bold text-slate-800">R$ 15.000,00</span>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs p-2 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="font-bold text-slate-700">Mensalidade recorrente Junho</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-500 font-semibold">14/06/2026</span>
                  <span className="font-bold text-slate-800">R$ 15.000,00</span>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs p-2 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                  <span className="font-bold text-slate-700">Aditivo Técnico Homologação (Pendente)</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-rose-500 font-bold">Vence em 13 dias</span>
                  <span className="font-bold text-slate-800">R$ 1.250,00</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Panel */}
        <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.01)] flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <FileText size={15} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#0F172A]">Documentos da Conta</h2>
                <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Arquivos juridicos, propostas comerciais e escopos vinculados</p>
              </div>
            </div>

            <button 
              onClick={() => setIsNewDocModalOpen(true)}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
            >
              <Plus size={12} />
              <span>Anexar Arquivo</span>
            </button>
          </div>

          <div className="flex flex-col gap-2.5">
            {allDocuments.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 rounded-2xl border border-slate-100 hover:border-slate-200 bg-slate-50/20 hover:bg-white transition-all group shadow-2xs">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold text-[9px]">
                    {doc.type}
                  </div>
                  <div className="flex flex-col gap-0.5 text-left">
                    <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-600 transition-colors line-clamp-1">{doc.title}</span>
                    <div className="flex items-center gap-2 text-[9px] font-semibold text-slate-400">
                      <span>Categoria: {doc.category}</span>
                      <span>•</span>
                      <span>Carregado em {doc.date}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-400 mr-2">{doc.size}</span>
                  <button className="p-1.5 rounded-lg text-slate-400 hover:text-[#0F172A] hover:bg-slate-100 transition-colors cursor-pointer" title="Visualizar">
                    <Eye size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- BACK TO LIST FOOTER --- */}
      <div className="flex items-center justify-center pt-4">
        <button 
          onClick={() => {
            setGlobalFilters(prev => ({ ...prev, clientId: undefined }));
            setCurrentView('clientes');
          }}
          className="text-xs font-bold text-[#64748B] hover:text-[#0F172A] flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all cursor-pointer"
        >
          <ArrowLeft size={13} />
          <span>Voltar para Gestão Geral de Clientes</span>
        </button>
      </div>

      {/* --- MODALS --- */}
      <AnimatePresence>
        {isEditModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEditModalOpen(false)}
              className="fixed inset-0 bg-black/30 backdrop-blur-xs"
            />
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              className="bg-white border border-[#0F172A0F] rounded-[24px] shadow-2xl z-10 overflow-hidden flex flex-col max-w-lg w-full max-h-[90vh]"
            >
              <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <Edit3 size={14} strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#111111]">Editar Ficha de Cadastro</h3>
                    <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Gerencie os dados primários do relacionamento</p>
                  </div>
                </div>
                <button onClick={() => setIsEditModalOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>

              <form onSubmit={handleSaveClientInfo} className="p-6 flex-1 overflow-y-auto flex flex-col gap-4 text-left">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Nome Completo</label>
                  <input type="text" required value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Cargo / Função</label>
                  <input type="text" required value={editFormData.role} onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">E-mail</label>
                    <input type="email" required value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Telefone</label>
                    <input type="text" required value={editFormData.phone} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Status Comercial</label>
                  <select value={editFormData.status} onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-bold">
                    <option value="Ativo">Ativo</option>
                    <option value="Lead">Lead / Prospect</option>
                    <option value="Inativo">Inativo</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Anotações do relacionamento</label>
                  <textarea rows={3} value={editFormData.notes} onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 resize-none font-semibold" />
                </div>
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-xl cursor-pointer">Cancelar</button>
                  <button type="submit" className="px-4 py-2 text-xs font-bold text-white bg-black hover:bg-[#1E293B] rounded-xl cursor-pointer">Salvar Alterações</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isNewTaskModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsNewTaskModalOpen(false)} className="fixed inset-0 bg-black/30 backdrop-blur-xs" />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 15 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 15 }} className="bg-white border border-[#0F172A0F] rounded-[24px] shadow-2xl z-10 overflow-hidden flex flex-col max-w-md w-full">
              <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><ListChecks size={14} strokeWidth={2.5} /></div>
                  <div><h3 className="text-sm font-bold text-[#111111]">Adicionar Nova Atividade</h3><p className="text-[10px] font-medium text-[#64748B] mt-0.5">Agende um follow-up tático de relacionamento</p></div>
                </div>
                <button onClick={() => setIsNewTaskModalOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"><X size={15} strokeWidth={2.5} /></button>
              </div>
              <form onSubmit={handleCreateTask} className="p-6 flex flex-col gap-4 text-left">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Título da Atividade</label>
                  <input type="text" required placeholder="Ex: Enviar orçamento comercial ou agendar call" value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Prioridade</label>
                  <select value={newTaskPriority} onChange={(e) => setNewTaskPriority(e.target.value)} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-bold">
                    <option value="Alta">Alta prioridade</option>
                    <option value="Média">Média</option>
                    <option value="Baixa">Baixa</option>
                  </select>
                </div>
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setIsNewTaskModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-xl cursor-pointer">Cancelar</button>
                  <button type="submit" className="px-4 py-2 text-xs font-bold text-white bg-black hover:bg-[#1E293B] rounded-xl cursor-pointer">Criar Atividade</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isNewProjectModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsNewProjectModalOpen(false)} className="fixed inset-0 bg-black/30 backdrop-blur-xs" />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 15 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 15 }} className="bg-white border border-[#0F172A0F] rounded-[24px] shadow-2xl z-10 overflow-hidden flex flex-col max-w-md w-full">
              <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
                <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><Briefcase size={14} strokeWidth={2.5} /></div><div><h3 className="text-sm font-bold text-[#111111]">Adicionar Projeto Vinculado</h3><p className="text-[10px] font-medium text-[#64748B] mt-0.5">Adicione um novo contrato de projeto técnico ao cliente</p></div></div>
                <button onClick={() => setIsNewProjectModalOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"><X size={15} strokeWidth={2.5} /></button>
              </div>
              <form onSubmit={handleCreateProject} className="p-6 flex flex-col gap-4 text-left">
                <div className="flex flex-col gap-1.5"><label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Nome do Projeto</label><input type="text" required placeholder="Ex: Integração Hub de Automação" value={newProjName} onChange={(e) => setNewProjName(e.target.value)} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" /></div>
                <div className="flex flex-col gap-1.5"><label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Prazo de Entrega</label><input type="text" placeholder="Ex: 15 Dez 2026" value={newProjDueDate} onChange={(e) => setNewProjDueDate(e.target.value)} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" /></div>
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setIsNewProjectModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-xl cursor-pointer">Cancelar</button>
                  <button type="submit" className="px-4 py-2 text-xs font-bold text-white bg-black hover:bg-[#1E293B] rounded-xl cursor-pointer">Iniciar Projeto</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isNewDocModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsNewDocModalOpen(false)} className="fixed inset-0 bg-black/30 backdrop-blur-xs" />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 15 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 15 }} className="bg-white border border-[#0F172A0F] rounded-[24px] shadow-2xl z-10 overflow-hidden flex flex-col max-w-md w-full">
              <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
                <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><FileText size={14} strokeWidth={2.5} /></div><div><h3 className="text-sm font-bold text-[#111111]">Vincular Novo Documento</h3><p className="text-[10px] font-medium text-[#64748B] mt-0.5">Anexe arquivos jurídicos ou propostas no Workspace</p></div></div>
                <button onClick={() => setIsNewDocModalOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"><X size={15} strokeWidth={2.5} /></button>
              </div>
              <form onSubmit={handleCreateDoc} className="p-6 flex flex-col gap-4 text-left">
                <div className="flex flex-col gap-1.5"><label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Título do Arquivo</label><input type="text" required placeholder="Ex: Acordo de Confidencialidade Técnico v1" value={newDocTitle} onChange={(e) => setNewDocTitle(e.target.value)} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-semibold" /></div>
                <div className="flex flex-col gap-1.5"><label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Categoria</label>
                  <select value={newDocCategory} onChange={(e) => setNewDocCategory(e.target.value)} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-bold">
                    <option value="Contrato">Contrato</option><option value="Proposta">Proposta Comercial</option><option value="NDA">NDA (Acordo de Confidencialidade)</option><option value="Escopo">Escopo Técnico</option><option value="Aditivo">Aditivo de Serviços</option>
                  </select>
                </div>
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setIsNewDocModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-xl cursor-pointer">Cancelar</button>
                  <button type="submit" className="px-4 py-2 text-xs font-bold text-white bg-black hover:bg-[#1E293B] rounded-xl cursor-pointer">Salvar Arquivo</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
